import { useEffect, useRef, useState } from 'react';
import { Heart } from 'lucide-react';
import { useLocation } from 'wouter';
import Modal from '@/components/common/Modal';
import SignInOptions from '@/components/auth/SignInOptions';
import { ToastAction } from '@/components/ui/toast';
import { AddToListsSheet } from '@/components/my-hub/AddToListsSheet';
import { useAuth } from '@/hooks/use-auth';
import { useSaved } from '@/hooks/use-saved';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface SaveButtonProps {
  productId: string;
  productName?: string;
  className?: string;
}

export default function SaveButton({
  productId,
  productName,
  className,
}: SaveButtonProps) {
  const { customer, setCustomer } = useAuth();
  const saved = useSaved();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [signInOpen, setSignInOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  // After inline sign-in we need to replay the save on the next render —
  // useSaved's mutation reads `customer` from useAuth, which only flips after
  // setCustomer(c) commits. Calling toggleSave synchronously inside onSuccess
  // would race the auth context update.
  const pendingSaveRef = useRef(false);

  useEffect(() => {
    if (!pulse) return;
    const t = window.setTimeout(() => setPulse(false), 350);
    return () => window.clearTimeout(t);
  }, [pulse]);

  // Replay the save once the customer is set after inline sign-in.
  useEffect(() => {
    if (!pendingSaveRef.current || !customer) return;
    pendingSaveRef.current = false;
    if (!saved.isSaved(productId)) {
      saved.toggleSave(productId);
    }
    setPickerOpen(true);
    setPulse(true);
    toast({
      title: 'Saved',
      description: 'It’s in your hub whenever you want it.',
      action: (
        <ToastAction altText="View in My Hub" onClick={() => setLocation('/my-hub')}>
          View
        </ToastAction>
      ),
    });
  }, [customer, productId, saved, setLocation, toast]);

  const isSaved = saved.isSaved(productId);
  const item = saved.items.find((i) => i.productId === productId);
  const memberships = item?.collectionIds ?? [];

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!customer) {
      setSignInOpen(true);
      return;
    }
    const wasSaved = isSaved;
    saved.toggleSave(productId);
    if (!wasSaved) {
      // Save transition only — unsave never opens the picker. The picker is
      // opt-in: dismissing it leaves the piece untagged. The save itself is
      // already committed by the optimistic update above, so a missed
      // pick costs nothing.
      setPickerOpen(true);
      setPulse(true);
    }
  };

  return (
    <>
      <AddToListsSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        collections={saved.collections}
        selected={memberships}
        onAdd={(cid) => saved.addToCollection(productId, cid)}
        onRemove={(cid) => saved.removeFromCollection(productId, cid)}
        onCreate={saved.createCollection}
      >
        <button
          type="button"
          onClick={handleClick}
          aria-pressed={isSaved}
          aria-label={isSaved ? 'Remove from saved pieces' : 'Save piece'}
          title={
            productName
              ? isSaved
                ? `Remove ${productName} from saved`
                : `Save ${productName}`
              : undefined
          }
          className={cn(
            'absolute z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm transition-all duration-200 hover:bg-white hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-soft-blush focus-visible:ring-offset-1 dark:bg-gray-900/90 dark:hover:bg-gray-900',
            className,
          )}
        >
          <Heart
            fill={isSaved ? 'currentColor' : 'none'}
            className={cn(
              'h-4 w-4 transition-all duration-200',
              isSaved ? 'text-soft-blush' : 'text-charcoal dark:text-white',
              // Brief scale-up on the save transition so the heart "pops"
              // before the picker is even noticed — signals 'the save
              // happened' independently of whether the customer engages
              // with the picker.
              pulse && 'scale-125',
            )}
          />
        </button>
      </AddToListsSheet>

      <Modal
        open={signInOpen}
        onOpenChange={(open) => {
          setSignInOpen(open);
          if (!open) pendingSaveRef.current = false;
        }}
        title="Sign in to save pieces"
        description="Saved pieces follow you across devices and form the start of your own wardrobe. Sign in and we’ll keep you right here."
      >
        <div className="mt-6">
          <SignInOptions
            signupSource="save_button"
            onSuccess={(c) => {
              pendingSaveRef.current = true;
              setSignInOpen(false);
              setCustomer(c);
            }}
          />
        </div>
      </Modal>
    </>
  );
}
